/**
 * LAWITY - Enhanced Client List Page
 * Full-featured client management with expanded data model
 */

import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Search, Plus, Filter, MoreVertical, Mail, Phone,
  Building, User, MapPin, Edit, Trash2, FileText, Briefcase,
  ChevronRight, ChevronDown, X, Check, Loader2, DollarSign,
  Calendar, Clock, AlertCircle, CheckCircle, Shield, Eye,
  Download, Upload, RefreshCw, LayoutGrid, List, SortAsc,
  SortDesc, Star, StarOff
} from 'lucide-react';
import { useAuth } from '../store/authStore';
import { clientService, relatedPartyService, auditService } from '../services/practiceService';
import { ClientForm } from '../components/practice/ClientForm';
import { RelatedPartiesManager } from '../components/practice/RelatedPartiesManager';
import type {
  Client,
  ClientFormData,
  ClientStatus,
  ClientType,
  RelatedParty,
} from '../types/practice';

// Status configuration
const STATUS_CONFIG: Record<ClientStatus, { label: string; color: string; bgColor: string }> = {
  prospect: { label: 'Prospect', color: 'text-blue-400', bgColor: 'bg-blue-500/20' },
  active: { label: 'Active', color: 'text-green-400', bgColor: 'bg-green-500/20' },
  inactive: { label: 'Inactive', color: 'text-gray-400', bgColor: 'bg-gray-500/20' },
  former: { label: 'Former', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' },
  declined: { label: 'Declined', color: 'text-red-400', bgColor: 'bg-red-500/20' },
  conflict: { label: 'Conflict', color: 'text-orange-400', bgColor: 'bg-orange-500/20' },
};

// Mock data for development (will be replaced with Firestore data)
const mockClients: Client[] = [
  {
    id: '1',
    clientNumber: 'CLT-000001',
    type: 'individual',
    status: 'active',
    firstName: 'John',
    lastName: 'Smith',
    email: 'john.smith@email.com',
    phone: '(920) 555-1234',
    preferredContactMethod: 'email',
    primaryAddress: {
      street1: '123 Main Street',
      city: 'Sturgeon Bay',
      state: 'WI',
      postalCode: '54235',
      country: 'USA',
    },
    mailingAddressSameAsPrimary: true,
    billingAddressSameAsPrimary: true,
    billingMethod: 'hourly',
    paymentTerms: 'net_30',
    defaultBillingRate: 350,
    taxExempt: false,
    engagementLetterSigned: true,
    conflictCheckCompleted: true,
    identityVerified: true,
    portalAccessEnabled: true,
    receiveInvoiceEmails: true,
    receiveMatterUpdates: true,
    receiveMarketingEmails: false,
    matterCount: 3,
    documentCount: 12,
    outstandingBalance: 2500,
    createdAt: new Date('2024-01-15'),
    createdBy: 'admin',
    updatedAt: new Date('2024-11-20'),
    updatedBy: 'admin',
    version: 5,
  },
  {
    id: '2',
    clientNumber: 'CLT-000002',
    type: 'entity',
    status: 'active',
    entityName: 'ABC Holdings LLC',
    entityType: 'llc',
    stateOfFormation: 'WI',
    email: 'info@abcholdings.com',
    phone: '(920) 555-5678',
    preferredContactMethod: 'email',
    primaryAddress: {
      street1: '456 Business Park Dr',
      city: 'Green Bay',
      state: 'WI',
      postalCode: '54301',
      country: 'USA',
    },
    mailingAddressSameAsPrimary: true,
    billingAddressSameAsPrimary: true,
    billingMethod: 'retainer',
    paymentTerms: 'net_30',
    defaultBillingRate: 400,
    retainerRequired: 5000,
    taxExempt: false,
    engagementLetterSigned: true,
    conflictCheckCompleted: true,
    identityVerified: true,
    portalAccessEnabled: true,
    receiveInvoiceEmails: true,
    receiveMatterUpdates: true,
    receiveMarketingEmails: true,
    matterCount: 5,
    documentCount: 24,
    outstandingBalance: 0,
    trustBalance: 5000,
    createdAt: new Date('2023-06-20'),
    createdBy: 'admin',
    updatedAt: new Date('2024-11-15'),
    updatedBy: 'admin',
    version: 12,
  },
  {
    id: '3',
    clientNumber: 'CLT-000003',
    type: 'individual',
    status: 'prospect',
    firstName: 'Jane',
    lastName: 'Doe',
    email: 'jane.doe@email.com',
    phone: '(920) 555-9012',
    preferredContactMethod: 'phone',
    primaryAddress: {
      street1: '789 Oak Avenue',
      city: 'Appleton',
      state: 'WI',
      postalCode: '54911',
      country: 'USA',
    },
    mailingAddressSameAsPrimary: true,
    billingAddressSameAsPrimary: true,
    billingMethod: 'hourly',
    paymentTerms: 'due_on_receipt',
    taxExempt: false,
    engagementLetterSigned: false,
    conflictCheckCompleted: false,
    identityVerified: false,
    portalAccessEnabled: false,
    receiveInvoiceEmails: true,
    receiveMatterUpdates: true,
    receiveMarketingEmails: false,
    matterCount: 0,
    documentCount: 0,
    createdAt: new Date('2024-11-01'),
    createdBy: 'admin',
    updatedAt: new Date('2024-11-01'),
    updatedBy: 'admin',
    version: 1,
  },
  {
    id: '4',
    clientNumber: 'CLT-000004',
    type: 'entity',
    status: 'active',
    entityName: 'Johnson Family Trust',
    entityType: 'trust',
    email: 'trust@johnsonfamily.com',
    phone: '(920) 555-3456',
    preferredContactMethod: 'mail',
    primaryAddress: {
      street1: '321 Trust Lane',
      city: 'Sturgeon Bay',
      state: 'WI',
      postalCode: '54235',
      country: 'USA',
    },
    mailingAddressSameAsPrimary: true,
    billingAddressSameAsPrimary: true,
    billingMethod: 'flat_fee',
    paymentTerms: 'net_15',
    taxExempt: true,
    taxExemptNumber: 'TE-12345',
    engagementLetterSigned: true,
    conflictCheckCompleted: true,
    identityVerified: true,
    portalAccessEnabled: false,
    receiveInvoiceEmails: true,
    receiveMatterUpdates: false,
    receiveMarketingEmails: false,
    matterCount: 2,
    documentCount: 8,
    outstandingBalance: 1500,
    createdAt: new Date('2024-03-10'),
    createdBy: 'admin',
    updatedAt: new Date('2024-10-28'),
    updatedBy: 'admin',
    version: 3,
  },
];

type ViewMode = 'list' | 'grid';
type SortField = 'name' | 'clientNumber' | 'status' | 'createdAt' | 'outstandingBalance';
type SortDirection = 'asc' | 'desc';

export function ClientList() {
  const { user } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ClientStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<ClientType | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Modal states
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [editingClient, setEditingClient] = useState<Client | null>(null);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    try {
      const result = await clientService.list();
      // If Firestore returns data, use it; otherwise fall back to mock
      setClients(result.data.length > 0 ? result.data : mockClients);
    } catch (error) {
      console.error('Error loading clients:', error);
      // Fall back to mock data on error
      setClients(mockClients);
    }
    setLoading(false);
  };

  const getClientName = (client: Client): string => {
    return client.type === 'entity'
      ? client.entityName || ''
      : `${client.firstName} ${client.lastName}`;
  };

  const getClientInitials = (client: Client): string => {
    if (client.type === 'entity') {
      return client.entityName?.substring(0, 2).toUpperCase() || 'EN';
    }
    return `${client.firstName?.[0] || ''}${client.lastName?.[0] || ''}`.toUpperCase();
  };

  // Filter and sort clients
  const filteredClients = useMemo(() => {
    let result = clients.filter(client => {
      const name = getClientName(client).toLowerCase();
      const matchesSearch = !searchQuery ||
        name.includes(searchQuery.toLowerCase()) ||
        client.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.clientNumber.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
      const matchesType = typeFilter === 'all' || client.type === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });

    // Sort
    result.sort((a, b) => {
      let aVal: any, bVal: any;
      switch (sortField) {
        case 'name':
          aVal = getClientName(a).toLowerCase();
          bVal = getClientName(b).toLowerCase();
          break;
        case 'clientNumber':
          aVal = a.clientNumber;
          bVal = b.clientNumber;
          break;
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
        case 'createdAt':
          aVal = a.createdAt;
          bVal = b.createdAt;
          break;
        case 'outstandingBalance':
          aVal = a.outstandingBalance || 0;
          bVal = b.outstandingBalance || 0;
          break;
        default:
          aVal = getClientName(a).toLowerCase();
          bVal = getClientName(b).toLowerCase();
      }
      const compare = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortDirection === 'asc' ? compare : -compare;
    });

    return result;
  }, [clients, searchQuery, statusFilter, typeFilter, sortField, sortDirection]);

  // Stats
  const stats = {
    total: clients.length,
    active: clients.filter(c => c.status === 'active').length,
    prospects: clients.filter(c => c.status === 'prospect').length,
    totalOutstanding: clients.reduce((sum, c) => sum + (c.outstandingBalance || 0), 0),
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleCreateClient = async (data: ClientFormData) => {
    if (!user) return;
    try {
      const newClient = await clientService.create(data, user.id);
      setClients(prev => [newClient, ...prev]);
      setShowNewClientModal(false);
    } catch (error) {
      console.error('Error creating client:', error);
      // Fall back to local creation for demo
      const newClient: Client = {
        id: `new-${Date.now()}`,
        clientNumber: `CLT-${String(clients.length + 1).padStart(6, '0')}`,
        ...data,
        matterCount: 0,
        documentCount: 0,
        createdAt: new Date(),
        createdBy: user.id,
        updatedAt: new Date(),
        updatedBy: user.id,
        version: 1,
      } as Client;
      setClients(prev => [newClient, ...prev]);
      setShowNewClientModal(false);
    }
  };

  const handleUpdateClient = async (data: ClientFormData) => {
    if (!user || !editingClient) return;
    try {
      const updatedClient = await clientService.update(editingClient.id, data, user.id, editingClient);
      setClients(prev => prev.map(c => c.id === editingClient.id ? updatedClient : c));
      setEditingClient(null);
    } catch (error) {
      console.error('Error updating client:', error);
      // Fall back to local update for demo
      setClients(prev => prev.map(c =>
        c.id === editingClient.id
          ? { ...c, ...data, updatedAt: new Date(), updatedBy: user.id, version: c.version + 1 }
          : c
      ));
      setEditingClient(null);
    }
  };

  const handleDeleteClient = async (clientId: string) => {
    if (!user) return;
    if (!confirm('Are you sure you want to delete this client?')) return;
    try {
      await clientService.delete(clientId, user.id);
      setClients(prev => prev.filter(c => c.id !== clientId));
      if (selectedClient?.id === clientId) setSelectedClient(null);
    } catch (error) {
      console.error('Error deleting client:', error);
      // Fall back to local delete for demo
      setClients(prev => prev.filter(c => c.id !== clientId));
      if (selectedClient?.id === clientId) setSelectedClient(null);
    }
  };

  const getComplianceStatus = (client: Client) => {
    const items = [
      client.engagementLetterSigned,
      client.conflictCheckCompleted,
      client.identityVerified,
    ];
    const completed = items.filter(Boolean).length;
    return { completed, total: items.length };
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-primary-700">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Users className="w-7 h-7 text-accent-400" />
            Clients
          </h1>
          <p className="text-primary-400 mt-1">
            {stats.total} total · {stats.active} active · {stats.prospects} prospects
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={loadClients}
            className="p-2 text-primary-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowNewClientModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors"
          >
            <Plus className="w-5 h-5" />
            New Client
          </button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="px-6 py-4 border-b border-primary-700 space-y-4">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-400" />
            <input
              type="text"
              placeholder="Search by name, email, or client number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:outline-none focus:border-accent-500"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ClientStatus | 'all')}
            className="px-4 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:outline-none focus:border-accent-500"
          >
            <option value="all">All Status</option>
            {Object.entries(STATUS_CONFIG).map(([value, config]) => (
              <option key={value} value={value}>{config.label}</option>
            ))}
          </select>

          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as ClientType | 'all')}
            className="px-4 py-2.5 bg-primary-800 border border-primary-600 text-white rounded-lg focus:outline-none focus:border-accent-500"
          >
            <option value="all">All Types</option>
            <option value="individual">Individual</option>
            <option value="entity">Entity</option>
          </select>

          {/* View Toggle */}
          <div className="flex border border-primary-600 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2.5 ${viewMode === 'list' ? 'bg-accent-500 text-white' : 'bg-primary-800 text-primary-400 hover:text-white'}`}
            >
              <List className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2.5 ${viewMode === 'grid' ? 'bg-accent-500 text-white' : 'bg-primary-800 text-primary-400 hover:text-white'}`}
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Active Filters */}
        {(statusFilter !== 'all' || typeFilter !== 'all' || searchQuery) && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-primary-400">Filters:</span>
            {searchQuery && (
              <span className="px-2 py-1 bg-primary-700 text-primary-300 text-sm rounded flex items-center gap-1">
                "{searchQuery}"
                <button onClick={() => setSearchQuery('')} className="hover:text-white">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {statusFilter !== 'all' && (
              <span className={`px-2 py-1 text-sm rounded flex items-center gap-1 ${STATUS_CONFIG[statusFilter].bgColor} ${STATUS_CONFIG[statusFilter].color}`}>
                {STATUS_CONFIG[statusFilter].label}
                <button onClick={() => setStatusFilter('all')} className="hover:opacity-75">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {typeFilter !== 'all' && (
              <span className="px-2 py-1 bg-primary-700 text-primary-300 text-sm rounded flex items-center gap-1">
                {typeFilter === 'individual' ? 'Individual' : 'Entity'}
                <button onClick={() => setTypeFilter('all')} className="hover:text-white">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            <button
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
                setTypeFilter('all');
              }}
              className="text-sm text-accent-400 hover:text-accent-300"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex">
        {/* Client List */}
        <div className={`${selectedClient ? 'w-1/2' : 'flex-1'} overflow-auto border-r border-primary-700`}>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-accent-400 animate-spin" />
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-primary-400">
              <Users className="w-16 h-16 mb-4 opacity-30" />
              <p className="text-lg">No clients found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : viewMode === 'list' ? (
            // List View
            <div className="divide-y divide-primary-700">
              {/* Sort Header */}
              <div className="flex items-center gap-4 px-4 py-2 bg-primary-800/50 text-xs font-medium text-primary-400 uppercase tracking-wider">
                <div className="w-12"></div>
                <button
                  onClick={() => handleSort('name')}
                  className="flex-1 flex items-center gap-1 hover:text-white"
                >
                  Name
                  {sortField === 'name' && (
                    sortDirection === 'asc' ? <SortAsc className="w-3 h-3" /> : <SortDesc className="w-3 h-3" />
                  )}
                </button>
                <button
                  onClick={() => handleSort('status')}
                  className="w-24 flex items-center gap-1 hover:text-white"
                >
                  Status
                  {sortField === 'status' && (
                    sortDirection === 'asc' ? <SortAsc className="w-3 h-3" /> : <SortDesc className="w-3 h-3" />
                  )}
                </button>
                <div className="w-20 text-center">Compliance</div>
                <div className="w-20 text-center">Matters</div>
                <button
                  onClick={() => handleSort('outstandingBalance')}
                  className="w-24 flex items-center gap-1 justify-end hover:text-white"
                >
                  Balance
                  {sortField === 'outstandingBalance' && (
                    sortDirection === 'asc' ? <SortAsc className="w-3 h-3" /> : <SortDesc className="w-3 h-3" />
                  )}
                </button>
                <div className="w-8"></div>
              </div>

              {filteredClients.map((client, idx) => {
                const isSelected = selectedClient?.id === client.id;
                const compliance = getComplianceStatus(client);
                return (
                  <motion.div
                    key={client.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.02 }}
                    className={`flex items-center gap-4 px-4 py-3 cursor-pointer transition-colors ${
                      isSelected ? 'bg-accent-500/10' : 'hover:bg-white/5'
                    }`}
                    onClick={() => setSelectedClient(client)}
                  >
                    {/* Avatar */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      client.type === 'entity' ? 'bg-purple-500/20' : 'bg-blue-500/20'
                    }`}>
                      {client.type === 'entity' ? (
                        <Building className="w-5 h-5 text-purple-400" />
                      ) : (
                        <span className="text-sm font-medium text-blue-400">
                          {getClientInitials(client)}
                        </span>
                      )}
                    </div>

                    {/* Name & Email */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white truncate">
                          {getClientName(client)}
                        </span>
                        <span className="text-xs text-primary-500">{client.clientNumber}</span>
                      </div>
                      <p className="text-sm text-primary-400 truncate">{client.email}</p>
                    </div>

                    {/* Status */}
                    <div className="w-24">
                      <span className={`px-2 py-0.5 rounded text-xs ${STATUS_CONFIG[client.status].bgColor} ${STATUS_CONFIG[client.status].color}`}>
                        {STATUS_CONFIG[client.status].label}
                      </span>
                    </div>

                    {/* Compliance */}
                    <div className="w-20 flex justify-center">
                      {compliance.completed === compliance.total ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <div className="flex items-center gap-1">
                          <AlertCircle className="w-4 h-4 text-yellow-400" />
                          <span className="text-xs text-yellow-400">
                            {compliance.completed}/{compliance.total}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Matters */}
                    <div className="w-20 text-center">
                      <span className="text-white">{client.matterCount || 0}</span>
                    </div>

                    {/* Balance */}
                    <div className="w-24 text-right">
                      {client.outstandingBalance ? (
                        <span className="text-amber-400">
                          ${client.outstandingBalance.toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-primary-500">-</span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="w-8">
                      <ChevronRight className="w-4 h-4 text-primary-500" />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            // Grid View
            <div className="p-4 grid grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredClients.map((client, idx) => {
                const compliance = getComplianceStatus(client);
                return (
                  <motion.div
                    key={client.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.02 }}
                    className="bg-primary-800/50 border border-primary-700 rounded-xl p-4 hover:border-accent-500/50 transition-colors cursor-pointer"
                    onClick={() => setSelectedClient(client)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        client.type === 'entity' ? 'bg-purple-500/20' : 'bg-blue-500/20'
                      }`}>
                        {client.type === 'entity' ? (
                          <Building className="w-6 h-6 text-purple-400" />
                        ) : (
                          <span className="text-lg font-medium text-blue-400">
                            {getClientInitials(client)}
                          </span>
                        )}
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs ${STATUS_CONFIG[client.status].bgColor} ${STATUS_CONFIG[client.status].color}`}>
                        {STATUS_CONFIG[client.status].label}
                      </span>
                    </div>

                    <h3 className="font-semibold text-white truncate">{getClientName(client)}</h3>
                    <p className="text-sm text-primary-400 truncate">{client.email}</p>

                    <div className="flex items-center gap-4 mt-4 text-sm">
                      <div className="flex items-center gap-1 text-primary-400">
                        <Briefcase className="w-4 h-4" />
                        <span>{client.matterCount || 0}</span>
                      </div>
                      <div className="flex items-center gap-1 text-primary-400">
                        <FileText className="w-4 h-4" />
                        <span>{client.documentCount || 0}</span>
                      </div>
                      {client.outstandingBalance ? (
                        <div className="flex items-center gap-1 text-amber-400 ml-auto">
                          <DollarSign className="w-4 h-4" />
                          <span>{client.outstandingBalance.toLocaleString()}</span>
                        </div>
                      ) : null}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Client Detail Panel */}
        <AnimatePresence>
          {selectedClient && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="w-1/2 overflow-auto bg-primary-900/50"
            >
              <ClientDetailPanel
                client={selectedClient}
                onClose={() => setSelectedClient(null)}
                onEdit={() => setEditingClient(selectedClient)}
                onDelete={() => handleDeleteClient(selectedClient.id)}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* New Client Modal */}
      <AnimatePresence>
        {showNewClientModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowNewClientModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-4xl h-[90vh] bg-primary-900 rounded-xl border border-primary-700 overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <ClientForm
                onSave={handleCreateClient}
                onCancel={() => setShowNewClientModal(false)}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Client Modal */}
      <AnimatePresence>
        {editingClient && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setEditingClient(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-4xl h-[90vh] bg-primary-900 rounded-xl border border-primary-700 overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <ClientForm
                initialData={editingClient as any}
                onSave={handleUpdateClient}
                onCancel={() => setEditingClient(null)}
                isEditing
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Client Detail Panel Component
function ClientDetailPanel({
  client,
  onClose,
  onEdit,
  onDelete,
}: {
  client: Client;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'billing' | 'compliance' | 'activity'>('overview');
  const [relatedParties, setRelatedParties] = useState<RelatedParty[]>([]);

  // Load related parties when client changes
  useEffect(() => {
    const loadRelatedParties = async () => {
      try {
        const parties = await relatedPartyService.getByClientId(client.id);
        setRelatedParties(parties);
      } catch (error) {
        console.error('Error loading related parties:', error);
      }
    };
    loadRelatedParties();
  }, [client.id]);

  const name = client.type === 'entity'
    ? client.entityName
    : `${client.firstName} ${client.lastName}`;

  const initials = client.type === 'entity'
    ? client.entityName?.substring(0, 2).toUpperCase() || 'EN'
    : `${client.firstName?.[0] || ''}${client.lastName?.[0] || ''}`.toUpperCase();

  const compliance = {
    engagement: client.engagementLetterSigned,
    conflicts: client.conflictCheckCompleted,
    identity: client.identityVerified,
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-primary-700">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
              client.type === 'entity' ? 'bg-purple-500/20' : 'bg-blue-500/20'
            }`}>
              {client.type === 'entity' ? (
                <Building className="w-7 h-7 text-purple-400" />
              ) : (
                <span className="text-xl font-medium text-blue-400">{initials}</span>
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">{name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-primary-400">{client.clientNumber}</span>
                <span className={`px-2 py-0.5 rounded text-xs ${STATUS_CONFIG[client.status].bgColor} ${STATUS_CONFIG[client.status].color}`}>
                  {STATUS_CONFIG[client.status].label}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onEdit}
              className="p-2 text-primary-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              title="Edit"
            >
              <Edit className="w-5 h-5" />
            </button>
            <button
              onClick={onDelete}
              className="p-2 text-primary-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
              title="Delete"
            >
              <Trash2 className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-primary-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4">
          {(['overview', 'billing', 'compliance', 'activity'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm capitalize transition-colors ${
                activeTab === tab
                  ? 'bg-accent-500/20 text-accent-400'
                  : 'text-primary-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-primary-800/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-white">{client.matterCount || 0}</p>
                <p className="text-xs text-primary-400">Matters</p>
              </div>
              <div className="bg-primary-800/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-white">{client.documentCount || 0}</p>
                <p className="text-xs text-primary-400">Documents</p>
              </div>
              <div className="bg-primary-800/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-amber-400">
                  ${(client.outstandingBalance || 0).toLocaleString()}
                </p>
                <p className="text-xs text-primary-400">Outstanding</p>
              </div>
              <div className="bg-primary-800/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-400">
                  ${(client.trustBalance || 0).toLocaleString()}
                </p>
                <p className="text-xs text-primary-400">Trust</p>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-sm font-medium text-primary-400 mb-3">Contact Information</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-3 text-primary-200">
                  <Mail className="w-4 h-4 text-primary-500" />
                  <a href={`mailto:${client.email}`} className="hover:text-accent-400">{client.email}</a>
                </div>
                {client.phone && (
                  <div className="flex items-center gap-3 text-primary-200">
                    <Phone className="w-4 h-4 text-primary-500" />
                    <a href={`tel:${client.phone}`} className="hover:text-accent-400">{client.phone}</a>
                  </div>
                )}
                <div className="flex items-start gap-3 text-primary-200">
                  <MapPin className="w-4 h-4 text-primary-500 mt-0.5" />
                  <div>
                    <p>{client.primaryAddress.street1}</p>
                    {client.primaryAddress.street2 && <p>{client.primaryAddress.street2}</p>}
                    <p>
                      {client.primaryAddress.city}, {client.primaryAddress.state} {client.primaryAddress.postalCode}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Related Parties */}
            <RelatedPartiesManager
              clientId={client.id}
              parties={relatedParties}
              onAdd={async (party) => {
                if (!user) return;
                try {
                  const newParty = await relatedPartyService.create(client.id, party, user.id);
                  setRelatedParties(prev => [...prev, newParty]);
                } catch (error) {
                  console.error('Error creating related party:', error);
                  // Fall back to local creation
                  setRelatedParties(prev => [...prev, { ...party, id: `rp-${Date.now()}`, clientId: client.id } as RelatedParty]);
                }
              }}
              onUpdate={async (partyId, data) => {
                if (!user) return;
                try {
                  await relatedPartyService.update(client.id, partyId, data, user.id);
                  setRelatedParties(prev => prev.map(p => p.id === partyId ? { ...p, ...data } : p));
                } catch (error) {
                  console.error('Error updating related party:', error);
                  setRelatedParties(prev => prev.map(p => p.id === partyId ? { ...p, ...data } : p));
                }
              }}
              onDelete={async (partyId) => {
                if (!user) return;
                try {
                  await relatedPartyService.delete(client.id, partyId, user.id);
                  setRelatedParties(prev => prev.filter(p => p.id !== partyId));
                } catch (error) {
                  console.error('Error deleting related party:', error);
                  setRelatedParties(prev => prev.filter(p => p.id !== partyId));
                }
              }}
            />
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium text-primary-400 mb-3">Billing Settings</h3>
              <div className="bg-primary-800/50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-primary-400">Billing Method</span>
                  <span className="text-white capitalize">{client.billingMethod.replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-primary-400">Default Rate</span>
                  <span className="text-white">${client.defaultBillingRate || 0}/hr</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-primary-400">Payment Terms</span>
                  <span className="text-white capitalize">{client.paymentTerms.replace('_', ' ')}</span>
                </div>
                {client.retainerRequired && (
                  <div className="flex justify-between">
                    <span className="text-primary-400">Retainer Required</span>
                    <span className="text-white">${client.retainerRequired.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-primary-400">Tax Exempt</span>
                  <span className={client.taxExempt ? 'text-green-400' : 'text-primary-500'}>
                    {client.taxExempt ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-primary-400 mb-3">Balance Summary</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <DollarSign className="w-6 h-6 text-amber-400 mb-2" />
                  <p className="text-2xl font-bold text-amber-400">
                    ${(client.outstandingBalance || 0).toLocaleString()}
                  </p>
                  <p className="text-sm text-amber-300/70">Outstanding Balance</p>
                </div>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <Shield className="w-6 h-6 text-green-400 mb-2" />
                  <p className="text-2xl font-bold text-green-400">
                    ${(client.trustBalance || 0).toLocaleString()}
                  </p>
                  <p className="text-sm text-green-300/70">Trust Balance</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'compliance' && (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <div className={`p-4 rounded-lg text-center ${
                compliance.engagement ? 'bg-green-500/10 border border-green-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'
              }`}>
                <CheckCircle className={`w-8 h-8 mx-auto mb-2 ${
                  compliance.engagement ? 'text-green-400' : 'text-yellow-400'
                }`} />
                <p className="font-medium text-white">Engagement Letter</p>
                <p className={`text-sm ${compliance.engagement ? 'text-green-400' : 'text-yellow-400'}`}>
                  {compliance.engagement ? 'Signed' : 'Pending'}
                </p>
              </div>
              <div className={`p-4 rounded-lg text-center ${
                compliance.conflicts ? 'bg-green-500/10 border border-green-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'
              }`}>
                <Shield className={`w-8 h-8 mx-auto mb-2 ${
                  compliance.conflicts ? 'text-green-400' : 'text-yellow-400'
                }`} />
                <p className="font-medium text-white">Conflict Check</p>
                <p className={`text-sm ${compliance.conflicts ? 'text-green-400' : 'text-yellow-400'}`}>
                  {compliance.conflicts ? 'Cleared' : 'Pending'}
                </p>
              </div>
              <div className={`p-4 rounded-lg text-center ${
                compliance.identity ? 'bg-green-500/10 border border-green-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'
              }`}>
                <User className={`w-8 h-8 mx-auto mb-2 ${
                  compliance.identity ? 'text-green-400' : 'text-yellow-400'
                }`} />
                <p className="font-medium text-white">Identity Verification</p>
                <p className={`text-sm ${compliance.identity ? 'text-green-400' : 'text-yellow-400'}`}>
                  {compliance.identity ? 'Verified' : 'Pending'}
                </p>
              </div>
            </div>

            {client.engagementDate && (
              <div className="bg-primary-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-primary-400">
                  <Calendar className="w-4 h-4" />
                  <span>Engagement Date</span>
                </div>
                <p className="text-white mt-1">
                  {new Date(client.engagementDate).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="space-y-4">
            <p className="text-primary-400 text-sm">Recent activity will appear here...</p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 text-sm">
                <div className="w-2 h-2 rounded-full bg-accent-400 mt-1.5" />
                <div>
                  <p className="text-white">Client created</p>
                  <p className="text-primary-500">{client.createdAt.toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <div className="w-2 h-2 rounded-full bg-primary-500 mt-1.5" />
                <div>
                  <p className="text-white">Last updated</p>
                  <p className="text-primary-500">{client.updatedAt.toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions Footer */}
      <div className="px-6 py-4 border-t border-primary-700 bg-primary-800/30">
        <div className="flex gap-3">
          <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors">
            <Briefcase className="w-4 h-4" />
            New Matter
          </button>
          <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-600 transition-colors">
            <FileText className="w-4 h-4" />
            New Document
          </button>
        </div>
      </div>
    </div>
  );
}
